import { applyMoveToBoard, boardToFen, colOf, findGeneral, generateLegalMoves, generatePseudoMoves, isInCheck, opposite, rowOf } from './engine';
import type { Board, Difficulty, GameState, Move, Personality, Piece, Side } from './types';
import { evaluateWithV1Pro, usesV1Pro } from './v1ProModel';
import { INITIAL_NON_KING_MATERIAL, MATERIAL } from './chessValues';

export const PERSONALITIES: Record<Difficulty, Personality> = {
  beginner: {
    id: 'beginner', name: '小棋童・阿芽', title: '初學', avatar: '芽',
    description: '看得見基本戰術與大子安全，仍會留下像真人初學時的空隙。', strength: '入門 V1',
    maxDepth: 2, timeMs: 420, nodeCap: 12_000, blunderRate: 0.18, lossBudget: 250, blunderBudget: 650, styleTemperature: 105,
    attackWeight: 0.72, defenseWeight: 0.64, mobilityWeight: 0.72, captureWeight: 1, tradeBias: 0,
    pieceSafetyWeight: 0.95, pawnWeight: 0.9, horseWeight: 0.94, cannonWeight: 0.94,
    knightCannonSynergyWeight: 0.35, centerWeight: 0.6, endgameWeight: 0.55,
    initiativeWeight: 0.6, pawnBreakthroughWeight: 0.7, blockadeWeight: 0.5, repertoireWeight: 0.6,
    openingBias: { P: 30, H: 16, C: 12 }, pointWin: 80, pointLoss: -18,
  },
  trader: {
    id: 'trader', name: '兌子・鐵算盤', title: '兌子', avatar: '兌',
    description: '見到等價交換就心動，會主動簡化局面；子少時反而容易被你算中。', strength: '初中階 V1-Pro',
    maxDepth: 4, timeMs: 700, nodeCap: 80_000, blunderRate: 0.07, lossBudget: 110, blunderBudget: 350, styleTemperature: 60,
    attackWeight: 0.8, defenseWeight: 1.02, mobilityWeight: 0.86, captureWeight: 1.34, tradeBias: 1.65,
    pieceSafetyWeight: 1.02, pawnWeight: 0.95, horseWeight: 0.96, cannonWeight: 1,
    knightCannonSynergyWeight: 0.55, centerWeight: 0.72, endgameWeight: 1.08,
    initiativeWeight: 0.7, pawnBreakthroughWeight: 0.7, blockadeWeight: 0.7, repertoireWeight: 0.8,
    openingBias: { C: 90, R: 30, P: 20 }, pointWin: 105, pointLoss: -28,
  },
  cannonhorse: {
    id: 'cannonhorse', name: '雙響・雷駒', title: '馬炮', avatar: '轟',
    description: '喜歡馬炮聯手、搶佔中路與製造炮架，招法活潑但不愛慢磨。', strength: '中階 V1-Pro',
    maxDepth: 5, timeMs: 850, nodeCap: 160_000, blunderRate: 0.03, lossBudget: 70, blunderBudget: 260, styleTemperature: 46,
    attackWeight: 1.28, defenseWeight: 0.78, mobilityWeight: 1.2, captureWeight: 1.02, tradeBias: -0.35,
    pieceSafetyWeight: 0.76, pawnWeight: 0.88, horseWeight: 1.18, cannonWeight: 1.2,
    knightCannonSynergyWeight: 1.75, centerWeight: 1.35, endgameWeight: 0.78,
    initiativeWeight: 0.95, pawnBreakthroughWeight: 0.75, blockadeWeight: 0.7, repertoireWeight: 0.9,
    openingBias: { H: 150, C: 150 }, pointWin: 125, pointLoss: -34,
  },
  gambiteer: {
    id: 'gambiteer', name: '焚舟・赤刃', title: '棄子', avatar: '焚',
    description: '願以兵馬換取先手，持續把火力送進將區；攻勢一斷，少掉的子就會成為代價。', strength: '中階棄子 V1-Pro',
    maxDepth: 6, timeMs: 1050, nodeCap: 320_000, blunderRate: 0.005, lossBudget: 16, blunderBudget: 110, styleTemperature: 24,
    attackWeight: 1.4, defenseWeight: 0.82, mobilityWeight: 1.25, captureWeight: 0.92, tradeBias: -0.7,
    pieceSafetyWeight: 0.78, pawnWeight: 1.08, horseWeight: 1.12, cannonWeight: 1.1,
    knightCannonSynergyWeight: 1.12, centerWeight: 1.3, endgameWeight: 0.65,
    initiativeWeight: 1.7, pawnBreakthroughWeight: 0.75, blockadeWeight: 0.5, repertoireWeight: 0.85,
    openingBias: { C: 135, H: 105, R: 70, P: 45 }, pointWin: 140, pointLoss: -39,
  },
  attacker: {
    id: 'attacker', name: '飛將・烈風', title: '攻殺', avatar: '烈',
    description: '重視過河、先手與將軍，敢棄兵搶攻，也可能攻過了頭。', strength: '進階攻殺 Pikafish',
    maxDepth: 5, timeMs: 840, nodeCap: 190_000, blunderRate: 0.035, lossBudget: 55, blunderBudget: 210, styleTemperature: 40,
    attackWeight: 1.58, defenseWeight: 0.7, mobilityWeight: 1.18, captureWeight: 1.12, tradeBias: -0.5,
    pieceSafetyWeight: 0.65, pawnWeight: 1.12, horseWeight: 1.05, cannonWeight: 1.08,
    knightCannonSynergyWeight: 1.08, centerWeight: 1.22, endgameWeight: 0.7,
    initiativeWeight: 1.25, pawnBreakthroughWeight: 0.9, blockadeWeight: 0.65, repertoireWeight: 0.9,
    openingBias: { C: 130, P: 105, H: 35 }, pointWin: 155, pointLoss: -44,
  },
  defender: {
    id: 'defender', name: '守城・玄壁', title: '防禦', avatar: '壁',
    description: '由 Pikafish 提供安全候選，再偏向士象、王安全與兌換攻子。', strength: '進階防守 Pikafish',
    maxDepth: 4, timeMs: 820, nodeCap: 180_000, blunderRate: 0.03, lossBudget: 50, blunderBudget: 210, styleTemperature: 40,
    attackWeight: 0.72, defenseWeight: 1.58, mobilityWeight: 0.92, captureWeight: 0.94, tradeBias: 0.48,
    pieceSafetyWeight: 1.35, pawnWeight: 0.95, horseWeight: 0.98, cannonWeight: 0.98,
    knightCannonSynergyWeight: 0.72, centerWeight: 0.78, endgameWeight: 1.05,
    initiativeWeight: 0.7, pawnBreakthroughWeight: 0.65, blockadeWeight: 1.15, repertoireWeight: 1,
    openingBias: { A: 310, E: 310, P: 20 }, pointWin: 170, pointLoss: -49,
  },
  pawn: {
    id: 'pawn', name: '渡河・青穗', title: '兵陣', avatar: '渡',
    description: '以小兵試路、封馬腿、守住中兵；Pikafish 先守住戰術底線，再讓五卒逐步連成壓力。', strength: '進階兵陣 Pikafish',
    maxDepth: 7, timeMs: 1350, nodeCap: 650_000, blunderRate: 0.004, lossBudget: 12, blunderBudget: 90, styleTemperature: 22,
    attackWeight: 1.08, defenseWeight: 1.08, mobilityWeight: 1.05, captureWeight: 0.96, tradeBias: 0.1,
    pieceSafetyWeight: 1.15, pawnWeight: 1.65, horseWeight: 0.95, cannonWeight: 0.95,
    knightCannonSynergyWeight: 0.75, centerWeight: 1.15, endgameWeight: 1.35,
    initiativeWeight: 0.85, pawnBreakthroughWeight: 2, blockadeWeight: 1.1, repertoireWeight: 1.1,
    openingBias: { P: 520, H: 30, E: 20 }, pointWin: 185, pointLoss: -53,
  },
  miser: {
    id: 'miser', name: '惜子・金庫', title: '惜子', avatar: '藏',
    description: '由 Pikafish 守住戰術底線，再偏向撤退、互保與保留大子。', strength: '進階惜子 Pikafish',
    maxDepth: 4, timeMs: 860, nodeCap: 200_000, blunderRate: 0.025, lossBudget: 38, blunderBudget: 170, styleTemperature: 34,
    attackWeight: 0.7, defenseWeight: 1.34, mobilityWeight: 0.82, captureWeight: 0.88, tradeBias: -0.18,
    pieceSafetyWeight: 1.9, pawnWeight: 0.9, horseWeight: 1.04, cannonWeight: 1.04,
    knightCannonSynergyWeight: 0.68, centerWeight: 0.68, endgameWeight: 1.08,
    initiativeWeight: 0.55, pawnBreakthroughWeight: 0.65, blockadeWeight: 1.05, repertoireWeight: 0.9,
    openingBias: { A: 390, E: 390, H: 10 }, pointWin: 200, pointLoss: -57,
  },
  blockader: {
    id: 'blockader', name: '鎖江・無渡', title: '封鎖', avatar: '鎖',
    description: '不急著吃子，而是封車路、卡馬腿、借炮線壓縮活動空間，讓對手逐步無路可走。', strength: '高階封鎖 Pikafish',
    maxDepth: 5, timeMs: 1040, nodeCap: 240_000, blunderRate: 0.012, lossBudget: 24, blunderBudget: 115, styleTemperature: 25,
    attackWeight: 0.9, defenseWeight: 1.35, mobilityWeight: 1.12, captureWeight: 0.96, tradeBias: 0.25,
    pieceSafetyWeight: 1.3, pawnWeight: 1.05, horseWeight: 1.08, cannonWeight: 1.18,
    knightCannonSynergyWeight: 1, centerWeight: 1.25, endgameWeight: 1.2,
    initiativeWeight: 0.85, pawnBreakthroughWeight: 0.9, blockadeWeight: 2, repertoireWeight: 0.95,
    openingBias: { C: 130, R: 105, H: 55, P: 25 }, pointWin: 220, pointLoss: -62,
  },
  scholar: {
    id: 'scholar', name: '墨痕・百譜', title: '布局', avatar: '譜',
    description: '熟悉古今譜路，會依紅黑方轉入不同陣式；脫譜後仍保留彈性，不讓開局像固定腳本。', strength: '高階布局 Pikafish',
    maxDepth: 6, timeMs: 1200, nodeCap: 350_000, blunderRate: 0.005, lossBudget: 12, blunderBudget: 80, styleTemperature: 18,
    attackWeight: 1, defenseWeight: 1.08, mobilityWeight: 1.12, captureWeight: 1, tradeBias: 0.12,
    pieceSafetyWeight: 1.18, pawnWeight: 1.08, horseWeight: 1.1, cannonWeight: 1.1,
    knightCannonSynergyWeight: 1.08, centerWeight: 1.22, endgameWeight: 1.15,
    initiativeWeight: 1, pawnBreakthroughWeight: 1.05, blockadeWeight: 1.15, repertoireWeight: 2,
    openingBias: { H: 90, C: 85, E: 75, P: 70, R: 35 }, pointWin: 235, pointLoss: -66,
  },
  veteran: {
    id: 'veteran', name: '殘局・秋燈', title: '殘局', avatar: '殘',
    description: 'Pikafish 中高節點設定；子力變少後更重視兵卒推進與收官。', strength: '高階殘局 Pikafish',
    maxDepth: 5, timeMs: 980, nodeCap: 230_000, blunderRate: 0.015, lossBudget: 25, blunderBudget: 125, styleTemperature: 28,
    attackWeight: 0.92, defenseWeight: 1.15, mobilityWeight: 1.02, captureWeight: 1.02, tradeBias: 0.52,
    pieceSafetyWeight: 1.25, pawnWeight: 1.25, horseWeight: 1.02, cannonWeight: 1,
    knightCannonSynergyWeight: 0.8, centerWeight: 0.88, endgameWeight: 1.72,
    initiativeWeight: 0.8, pawnBreakthroughWeight: 1.35, blockadeWeight: 1, repertoireWeight: 1,
    openingBias: { P: 420, K: 20 }, pointWin: 250, pointLoss: -70,
  },
  master: {
    id: 'master', name: '守關人・雲隱', title: '大師', avatar: '隱',
    description: 'Pikafish 封頂設定，優先採最佳著法；正式級分以混合引擎校準結果為準。', strength: '守關大師 Pikafish',
    maxDepth: 5, timeMs: 1100, nodeCap: 250_000, blunderRate: 0, lossBudget: 10, blunderBudget: 80, styleTemperature: 18,
    attackWeight: 1.05, defenseWeight: 1.12, mobilityWeight: 1.07, captureWeight: 1.04, tradeBias: 0.25,
    pieceSafetyWeight: 1.25, pawnWeight: 1.08, horseWeight: 1.06, cannonWeight: 1.06,
    knightCannonSynergyWeight: 1.05, centerWeight: 1.05, endgameWeight: 1.28,
    initiativeWeight: 1.1, pawnBreakthroughWeight: 1.1, blockadeWeight: 1.1, repertoireWeight: 1.2,
    openingBias: { H: 40, C: 35, P: 30, E: 20 }, pointWin: 280, pointLoss: -78,
  },
};

// 搜尋層一律使用同一組局面評估；角色差異只在根節點的合格候選池內重排。
// 這讓 depth/nodeCap/lossBudget 成為可校準的強度旋鈕，棋風不再污染整棵搜尋樹。
const NEUTRAL_SEARCH_PERSONALITY: Personality = {
  ...PERSONALITIES.master,
  attackWeight: 1,
  defenseWeight: 1,
  mobilityWeight: 1,
  captureWeight: 1,
  tradeBias: 0,
  pieceSafetyWeight: 1,
  pawnWeight: 1,
  horseWeight: 1,
  cannonWeight: 1,
  knightCannonSynergyWeight: 1,
  centerWeight: 1,
  endgameWeight: 1,
  initiativeWeight: 1,
  pawnBreakthroughWeight: 1,
  blockadeWeight: 1,
  repertoireWeight: 1,
  openingBias: {},
};

const MATE = 1_000_000;
const MATE_SCORE_THRESHOLD = MATE - 10_000;

type TranspositionFlag = 'exact' | 'lower' | 'upper';

interface TranspositionEntry {
  depth: number;
  score: number;
  flag: TranspositionFlag;
  bestMove?: Move;
}

interface SearchContext {
  deadline: number;
  nodes: number;
  stopped: boolean;
  nodeCap: number;
  table: Map<string, TranspositionEntry>;
  killers: Array<[Move | null, Move | null]>;
  history: Int32Array;
  useLearnedEval: boolean;
  learnedBlend?: number;
}

export interface SearchSummary {
  move: Move | null;
  depth: number;
  nodes: number;
  score: number;
  elapsedMs: number;
}

function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function scoreToTable(score: number, ply: number): number {
  if (score >= MATE_SCORE_THRESHOLD) return score + ply;
  if (score <= -MATE_SCORE_THRESHOLD) return score - ply;
  return score;
}

function scoreFromTable(score: number, ply: number): number {
  if (score >= MATE_SCORE_THRESHOLD) return score - ply;
  if (score <= -MATE_SCORE_THRESHOLD) return score + ply;
  return score;
}

function sideFactor(side: Side): number {
  return side === 'red' ? 1 : -1;
}

function crossedRiver(piece: Piece, row: number): boolean {
  return piece.side === 'red' ? row <= 4 : row >= 5;
}

function attackPressure(board: Board, side: Side, inCheck: boolean): number {
  const enemyGeneral = findGeneral(board, opposite(side));
  if (enemyGeneral < 0) return 10_000;
  const er = rowOf(enemyGeneral);
  const ec = colOf(enemyGeneral);
  let pressure = 0;
  for (let index = 0; index < board.length; index += 1) {
    const piece = board[index];
    if (!piece || piece.side !== side || ['K', 'A', 'E'].includes(piece.type)) continue;
    const distance = Math.abs(rowOf(index) - er) + Math.abs(colOf(index) - ec);
    pressure += Math.max(0, 10 - distance) * (piece.type === 'R' ? 4 : piece.type === 'C' ? 3 : 2);
    if (crossedRiver(piece, rowOf(index))) pressure += piece.type === 'P' ? 18 : 8;
  }
  if (inCheck) pressure += 45;
  return pressure;
}

function defenseShape(board: Board, side: Side, inCheck: boolean): number {
  const general = findGeneral(board, side);
  if (general < 0) return -10_000;
  const gr = rowOf(general);
  const gc = colOf(general);
  let score = 0;
  for (let index = 0; index < board.length; index += 1) {
    const piece = board[index];
    if (!piece || piece.side !== side) continue;
    if (piece.type === 'A' || piece.type === 'E') score += 24;
    if (piece.type !== 'K') {
      const distance = Math.abs(rowOf(index) - gr) + Math.abs(colOf(index) - gc);
      if (distance <= 2) score += 6;
    }
  }
  if (inCheck) score -= 70;
  return score;
}

function boardActivity(board: Board, side: Side): { attacked: Set<number>; mobility: number } {
  const attacked = new Set<number>();
  let mobility = 0;
  for (let index = 0; index < board.length; index += 1) {
    if (board[index]?.side !== side) continue;
    const moves = generatePseudoMoves(board, index);
    mobility += moves.length;
    for (const move of moves) attacked.add(move.to);
  }
  return { attacked, mobility };
}

function attackMap(board: Board, side: Side): Set<number> {
  return boardActivity(board, side).attacked;
}

function knightCannonSynergy(board: Board, side: Side): number {
  const horses: number[] = [];
  const cannons: number[] = [];
  board.forEach((piece, index) => {
    if (piece?.side !== side) return;
    if (piece.type === 'H') horses.push(index);
    if (piece.type === 'C') cannons.push(index);
  });
  let score = 0;
  for (const horse of horses) {
    for (const cannon of cannons) {
      const distance = Math.abs(rowOf(horse) - rowOf(cannon)) + Math.abs(colOf(horse) - colOf(cannon));
      score += Math.max(0, 7 - distance) * 4;
    }
  }
  return score;
}

function endgameActivity(board: Board, side: Side): number {
  const general = findGeneral(board, side);
  let score = general < 0 ? -500 : 12 - Math.abs(4 - colOf(general)) * 3;
  board.forEach((piece, index) => {
    if (piece?.side !== side) return;
    if (piece.type === 'P') {
      const advance = side === 'red' ? 9 - rowOf(index) : rowOf(index);
      score += advance * 5 + (crossedRiver(piece, rowOf(index)) ? 14 : 0);
    }
    if (piece.type === 'R' || piece.type === 'H') score += Math.max(0, 4 - Math.abs(4 - colOf(index))) * 2;
  });
  return score;
}

function evaluateRed(board: Board, personality: Personality): number {
  let score = 0;
  let baseMaterialDiff = 0;
  let remainingMaterial = 0;
  let redCenter = 0;
  let blackCenter = 0;
  const redActivity = boardActivity(board, 'red');
  const blackActivity = boardActivity(board, 'black');
  const redAttacks = redActivity.attacked;
  const blackAttacks = blackActivity.attacked;
  const redInCheck = isInCheck(board, 'red');
  const blackInCheck = isInCheck(board, 'black');
  for (let index = 0; index < board.length; index += 1) {
    const piece = board[index];
    if (!piece) continue;
    const factor = sideFactor(piece.side);
    let value = MATERIAL[piece.type];
    if (piece.type !== 'K') {
      baseMaterialDiff += factor * MATERIAL[piece.type];
      remainingMaterial += MATERIAL[piece.type];
    }
    const row = rowOf(index);
    const col = colOf(index);
    if (piece.type === 'P') {
      const advance = piece.side === 'red' ? 9 - row : row;
      value = (value + advance * 11 + (crossedRiver(piece, row) ? 38 : 0)) * personality.pawnWeight;
    }
    if (piece.type === 'H') value = (value + Math.max(0, 4 - Math.abs(4 - col)) * 5) * personality.horseWeight;
    if (piece.type === 'C') value = (value + Math.max(0, 4 - Math.abs(4 - col)) * 5) * personality.cannonWeight;
    score += factor * value;
    const center = Math.max(0, 4 - Math.abs(4 - col));
    if (piece.side === 'red') redCenter += center;
    else blackCenter += center;
    if (piece.type !== 'K') {
      const enemyAttacks = piece.side === 'red' ? blackAttacks : redAttacks;
      const friendlyAttacks = piece.side === 'red' ? redAttacks : blackAttacks;
      if (enemyAttacks.has(index)) {
        const danger = friendlyAttacks.has(index) ? MATERIAL[piece.type] * 0.035 : MATERIAL[piece.type] * 0.11;
        score -= factor * danger * personality.pieceSafetyWeight;
      }
    }
  }
  score += (redActivity.mobility - blackActivity.mobility) * 3.4 * personality.mobilityWeight;
  score += (attackPressure(board, 'red', blackInCheck) - attackPressure(board, 'black', redInCheck)) * personality.attackWeight;
  score += (defenseShape(board, 'red', redInCheck) - defenseShape(board, 'black', blackInCheck)) * personality.defenseWeight;
  score += (redCenter - blackCenter) * 4.5 * personality.centerWeight;
  score += (knightCannonSynergy(board, 'red') - knightCannonSynergy(board, 'black')) * personality.knightCannonSynergyWeight;
  const endgamePhase = Math.max(0, 1 - remainingMaterial / INITIAL_NON_KING_MATERIAL);
  score += baseMaterialDiff * personality.tradeBias * endgamePhase * 0.14;
  score += (endgameActivity(board, 'red') - endgameActivity(board, 'black')) * personality.endgameWeight * endgamePhase;
  return Math.round(score);
}

export function evaluateV1Baseline(board: Board): number {
  return evaluateRed(board, NEUTRAL_SEARCH_PERSONALITY);
}

function evaluateFor(board: Board, side: Side, useLearnedEval = false, learnedBlend?: number): number {
  const baseline = evaluateRed(board, NEUTRAL_SEARCH_PERSONALITY);
  const score = useLearnedEval
    ? evaluateWithV1Pro(board, baseline, learnedBlend)
    : baseline;
  return side === 'red' ? score : -score;
}

function captureValue(board: Board, move: Move): number {
  const captured = board[move.to];
  const mover = board[move.from];
  return captured ? MATERIAL[captured.type] * 10 - (mover ? MATERIAL[mover.type] : 0) : 0;
}

function sameMove(a: Move | null | undefined, b: Move): boolean {
  return Boolean(a && a.from === b.from && a.to === b.to);
}

function moveOrderScore(board: Board, move: Move, ctx?: SearchContext, ply = 0, ttMove?: Move): number {
  if (sameMove(ttMove, move)) return 2_000_000;
  let score = captureValue(board, move) * 100;
  const next = applyMoveToBoard(board, move);
  const mover = board[move.from];
  if (mover && isInCheck(next, opposite(mover.side))) score += 120_000;
  if (!board[move.to] && ctx) {
    const killers = ctx.killers[ply];
    if (sameMove(killers?.[0], move)) score += 90_000;
    else if (sameMove(killers?.[1], move)) score += 70_000;
    score += ctx.history[move.from * 90 + move.to] ?? 0;
  }
  return score;
}

function orderMoves(board: Board, moves: Move[], ctx?: SearchContext, ply = 0, ttMove?: Move): Move[] {
  return moves.slice().sort((a, b) => moveOrderScore(board, b, ctx, ply, ttMove) - moveOrderScore(board, a, ctx, ply, ttMove));
}

function timeExpired(ctx: SearchContext): boolean {
  const shouldCheckClock = ctx.nodes === 1 || (ctx.nodes & 63) === 0;
  if (ctx.nodes >= ctx.nodeCap || (shouldCheckClock && performance.now() >= ctx.deadline)) ctx.stopped = true;
  return ctx.stopped;
}

function quiescence(board: Board, side: Side, alpha: number, beta: number, depth: number, ply: number, ctx: SearchContext): number {
  ctx.nodes += 1;
  if (timeExpired(ctx)) return evaluateFor(board, side, ctx.useLearnedEval, ctx.learnedBlend);
  const legalMoves = generateLegalMoves(board, side);
  if (legalMoves.length === 0) return -MATE + ply;
  const inCheck = isInCheck(board, side);
  const standPat = evaluateFor(board, side, ctx.useLearnedEval, ctx.learnedBlend);
  if (!inCheck) {
    if (standPat >= beta) return beta;
    if (standPat > alpha) alpha = standPat;
    if (depth <= 0) return alpha;
  }

  const tacticalMoves = inCheck ? orderMoves(board, legalMoves, ctx, ply) : orderMoves(board, legalMoves.filter((move) => board[move.to]), ctx, ply);
  for (const move of tacticalMoves) {
    const nextBoard = applyMoveToBoard(board, move);
    let score: number;
    if (inCheck && depth <= 0) {
      const nextSide = opposite(side);
      score = generateLegalMoves(nextBoard, nextSide).length === 0
        ? MATE - (ply + 1)
        : -evaluateFor(nextBoard, nextSide, ctx.useLearnedEval, ctx.learnedBlend);
    } else {
      score = -quiescence(nextBoard, opposite(side), -beta, -alpha, depth - 1, ply + 1, ctx);
    }
    if (ctx.stopped) return 0;
    if (score >= beta) return beta;
    if (score > alpha) alpha = score;
  }
  return alpha;
}

function negamax(board: Board, side: Side, depth: number, alpha: number, beta: number, ply: number, ctx: SearchContext): number {
  ctx.nodes += 1;
  if (timeExpired(ctx)) return evaluateFor(board, side, ctx.useLearnedEval, ctx.learnedBlend);
  const key = `${boardToFen(board)} ${side}`;
  const originalAlpha = alpha;
  const originalBeta = beta;
  const cached = ctx.table.get(key);
  if (cached && cached.depth >= depth) {
    const cachedScore = scoreFromTable(cached.score, ply);
    if (cached.flag === 'exact') return cachedScore;
    if (cached.flag === 'lower') alpha = Math.max(alpha, cachedScore);
    else beta = Math.min(beta, cachedScore);
    if (alpha >= beta) return cachedScore;
  }
  if (depth <= 0) return quiescence(board, side, alpha, beta, 3, ply, ctx);
  const moves = generateLegalMoves(board, side);
  if (moves.length === 0) return -MATE + ply;

  let best = -Infinity;
  let bestMove: Move | undefined;
  let searched = 0;
  for (const move of orderMoves(board, moves, ctx, ply, cached?.bestMove)) {
    const nextBoard = applyMoveToBoard(board, move);
    let score: number;
    if (searched === 0) {
      score = -negamax(nextBoard, opposite(side), depth - 1, -beta, -alpha, ply + 1, ctx);
    } else {
      score = -negamax(nextBoard, opposite(side), depth - 1, -alpha - 1, -alpha, ply + 1, ctx);
      if (!ctx.stopped && score > alpha && score < beta) {
        score = -negamax(nextBoard, opposite(side), depth - 1, -beta, -alpha, ply + 1, ctx);
      }
    }
    if (ctx.stopped) return 0;
    searched += 1;
    if (score > best) {
      best = score;
      bestMove = move;
    }
    if (score > alpha) alpha = score;
    if (alpha >= beta) {
      if (!board[move.to]) {
        const killers = ctx.killers[ply] ?? [null, null];
        if (!sameMove(killers[0], move)) ctx.killers[ply] = [move, killers[0]];
        const historyIndex = move.from * 90 + move.to;
        ctx.history[historyIndex] = Math.min(200_000, (ctx.history[historyIndex] ?? 0) + depth * depth * 32);
      }
      break;
    }
  }
  const flag: TranspositionFlag = best <= originalAlpha ? 'upper' : best >= originalBeta ? 'lower' : 'exact';
  ctx.table.set(key, { depth, score: scoreToTable(best, ply), flag, bestMove });
  return best;
}

function openingFlavor(board: Board, move: Move, personality: Personality, plyCount: number): number {
  const piece = board[move.from];
  if (!piece) return 0;
  const targetRow = rowOf(move.to);
  const targetCol = colOf(move.to);
  const center = 4 - Math.abs(4 - targetCol);
  const openingScale = Math.max(0, 1 - plyCount / 12);
  let score = center * 2 * personality.centerWeight;
  score += (personality.openingBias[piece.type] ?? 0) * openingScale;
  const captured = board[move.to];
  if (captured) {
    score += MATERIAL[captured.type] * 0.06 * personality.captureWeight;
    score += personality.tradeBias * 24;
  }
  if (piece.type === 'H') score += 12 * (personality.horseWeight - 0.9);
  if (piece.type === 'C') score += 12 * (personality.cannonWeight - 0.9);
  if (piece.type === 'P') score += 14 * (personality.pawnWeight - 0.85);
  let special = 0;
  if (personality.id === 'attacker') {
    const advance = piece.side === 'red' ? rowOf(move.from) - targetRow : targetRow - rowOf(move.from);
    special += advance * 18 + (piece.type === 'C' ? 14 : 0);
  }
  if (personality.id === 'defender') {
    special += ['A', 'E'].includes(piece.type) ? 24 : 0;
    special += board[move.to] ? 20 : 0;
  }
  if (personality.id === 'trader') special += captured ? 34 : -4;
  if (personality.id === 'miser') {
    const advance = piece.side === 'red' ? rowOf(move.from) - targetRow : targetRow - rowOf(move.from);
    special += ['A', 'E'].includes(piece.type) ? 18 : -Math.max(0, advance) * 4;
  }
  if (personality.id === 'cannonhorse') special += ['H', 'C'].includes(piece.type) ? 30 : 0;
  if (personality.id === 'gambiteer') {
    const advance = piece.side === 'red' ? rowOf(move.from) - targetRow : targetRow - rowOf(move.from);
    special += Math.max(0, advance) * 16 + (['C', 'H', 'R'].includes(piece.type) ? 16 : 0);
  }
  if (personality.id === 'pawn') special += piece.type === 'P' ? 58 : 0;
  if (personality.id === 'blockader') special += ['C', 'R'].includes(piece.type) ? 22 : 0;
  if (personality.id === 'scholar') special += ['H', 'C', 'E', 'P'].includes(piece.type) ? 15 : 0;
  if (personality.id === 'veteran') special += piece.type === 'P' ? 14 : ['A', 'E'].includes(piece.type) ? 9 : 0;
  if (personality.id === 'master') special += ['H', 'C', 'P'].includes(piece.type) ? 8 : 0;
  return score + special * openingScale;
}

// 棋風只在根節點的安全候選池中重排，不改動搜尋樹本身。
// 因此角色能有清楚偏好，卻不會為了演出棋風而錯過強制勝著或送掉大子。
export function personalityMoveBonus(
  board: Board,
  side: Side,
  move: Move,
  personality: Personality,
  plyCount: number,
): number {
  const mover = board[move.from];
  if (!mover || mover.side !== side) return -10_000;
  const enemy = opposite(side);
  const beforeOwn = boardActivity(board, side);
  const beforeEnemy = boardActivity(board, enemy);
  const next = applyMoveToBoard(board, move);
  const afterOwn = boardActivity(next, side);
  const afterEnemy = boardActivity(next, enemy);
  const givesCheck = isInCheck(next, enemy);
  const pressureBefore = attackPressure(board, side, isInCheck(board, enemy));
  const pressureAfter = attackPressure(next, side, givesCheck);
  const pressureGain = Math.max(-30, Math.min(80, pressureAfter - pressureBefore));
  const enemyMobilityLoss = Math.max(-20, Math.min(20, beforeEnemy.mobility - afterEnemy.mobility));
  const ownMobilityGain = Math.max(-20, Math.min(20, afterOwn.mobility - beforeOwn.mobility));
  let bonus = openingFlavor(board, move, personality, plyCount);

  bonus += pressureGain * 0.7 * personality.initiativeWeight;
  bonus += (givesCheck ? 48 : 0) * personality.initiativeWeight;
  bonus += enemyMobilityLoss * 4.5 * personality.blockadeWeight;
  bonus += ownMobilityGain * 1.8 * personality.repertoireWeight;

  if (mover.type === 'P') {
    const fromRow = rowOf(move.from);
    const toRow = rowOf(move.to);
    const advance = side === 'red' ? fromRow - toRow : toRow - fromRow;
    const newlyCrossed = !crossedRiver(mover, fromRow) && crossedRiver(mover, toRow);
    bonus += Math.max(0, advance) * 22 * personality.pawnBreakthroughWeight;
    if (newlyCrossed) bonus += 34 * personality.pawnBreakthroughWeight;
  }

  if (personality.id === 'gambiteer') {
    const enemyAttacks = attackMap(next, enemy);
    const friendlyAttacks = attackMap(next, side);
    const offered = enemyAttacks.has(move.to) && !friendlyAttacks.has(move.to);
    if (offered && (givesCheck || pressureGain >= 18)) bonus += 62;
    else if (offered) bonus -= 28;
  }
  if (personality.id === 'blockader' && (mover.type === 'C' || mover.type === 'R')) {
    bonus += Math.max(0, enemyMobilityLoss) * 5;
  }
  if (personality.id === 'scholar') {
    const openingScale = Math.max(0, 1 - plyCount / 20);
    bonus += Math.max(0, ownMobilityGain) * 4 * openingScale;
  }
  return bonus;
}

function softmaxPickByStyle(
  pool: Array<{ move: Move; score: number; style: number }>,
  random: () => number,
  temperature: number,
): { move: Move; score: number } | null {
  if (!pool.length) return null;
  const bestStyle = Math.max(...pool.map((candidate) => candidate.style));
  const weights = pool.map((candidate) => Math.exp((candidate.style - bestStyle) / Math.max(1, temperature)));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  let pick = random() * total;
  for (let index = 0; index < pool.length; index += 1) {
    pick -= weights[index] ?? 0;
    if (pick <= 0) return pool[index] ?? null;
  }
  const selected = pool[pool.length - 1];
  return selected ? { move: selected.move, score: selected.score } : null;
}

function tacticalPriority(board: Board, side: Side, move: Move): number {
  const mover = board[move.from];
  if (!mover || mover.side !== side) return 0;
  const captured = board[move.to];
  if (captured?.type === 'K') return 4;
  if (captured?.type === 'R') return 3;
  if (captured && (captured.type === 'H' || captured.type === 'C')) return 2;

  if (mover.type === 'R' || mover.type === 'H' || mover.type === 'C') {
    const enemy = opposite(side);
    if (attackMap(board, enemy).has(move.from)) {
      const nextBoard = applyMoveToBoard(board, move);
      if (!attackMap(nextBoard, enemy).has(move.to)) return mover.type === 'R' ? 3 : 2;
    }
  }
  return 0;
}

function selectPersonalityMove(
  candidates: Array<{ move: Move; score: number }>,
  personality: Personality,
  seed: number,
  board: Board,
  side: Side,
  plyCount: number,
  styleEnabled = true,
): { move: Move; score: number } {
  const sorted = candidates.slice().sort((a, b) => b.score - a.score);
  const best = sorted[0];
  if (!best) throw new Error('沒有可選走法');
  if (!styleEnabled) return best;
  const random = mulberry32(seed ^ 0x9e3779b9);
  const bestPriority = tacticalPriority(board, side, best.move);
  const tacticallySafe = bestPriority > 0
    ? sorted.filter((candidate) => tacticalPriority(board, side, candidate.move) >= bestPriority)
    : sorted;
  const budget = random() < personality.blunderRate ? personality.blunderBudget : personality.lossBudget;
  const pool = tacticallySafe.filter((candidate) => best.score - candidate.score <= budget).slice(0, 12);
  if (pool.length <= 1) return best;
  const styled = pool.map((candidate) => ({
    ...candidate,
    style: personalityMoveBonus(board, side, candidate.move, personality, plyCount),
  }));
  return softmaxPickByStyle(styled, random, personality.styleTemperature) ?? best;
}

export function findBestMove(
  state: GameState,
  difficulty: Difficulty,
  limits?: { maxDepth?: number; timeMs?: number; nodeCap?: number; styleEnabled?: boolean; useLearnedEval?: boolean; learnedBlend?: number },
): SearchSummary {
  const started = performance.now();
  const personality = PERSONALITIES[difficulty];
  const maxDepth = limits?.maxDepth ?? personality.maxDepth;
  const timeMs = limits?.timeMs ?? personality.timeMs;
  const legalMoves = generateLegalMoves(state.board, state.sideToMove);
  if (!legalMoves.length) return { move: null, depth: 0, nodes: 0, score: -MATE, elapsedMs: performance.now() - started };
  const ctx: SearchContext = {
    deadline: started + timeMs,
    nodes: 0,
    stopped: false,
    nodeCap: limits?.nodeCap ?? personality.nodeCap,
    table: new Map(),
    killers: Array.from({ length: 96 }, () => [null, null]),
    history: new Int32Array(90 * 90),
    useLearnedEval: limits?.useLearnedEval ?? usesV1Pro(difficulty),
    learnedBlend: limits?.learnedBlend,
  };
  let completedDepth = 0;
  let completedCandidates: Array<{ move: Move; score: number }> = orderMoves(state.board, legalMoves).map((move) => {
    const board = applyMoveToBoard(state.board, move);
    return {
      move,
      score: evaluateFor(board, state.sideToMove, ctx.useLearnedEval, ctx.learnedBlend),
    };
  });

  for (let depth = 1; depth <= maxDepth; depth += 1) {
    const current: Array<{ move: Move; score: number }> = [];
    for (const move of orderMoves(state.board, legalMoves, ctx, 0)) {
      const board = applyMoveToBoard(state.board, move);
      const baseScore = -negamax(board, opposite(state.sideToMove), depth - 1, -Infinity, Infinity, 1, ctx);
      if (ctx.stopped) break;
      current.push({ move, score: baseScore });
    }
    if (ctx.stopped || current.length !== legalMoves.length) break;
    completedCandidates = current;
    completedDepth = depth;
  }

  const selected = selectPersonalityMove(
    completedCandidates,
    personality,
    (state.seed + state.moves.length * 2654435761) >>> 0,
    state.board,
    state.sideToMove,
    state.moves.length,
    limits?.styleEnabled ?? true,
  );
  return {
    move: selected.move,
    depth: completedDepth,
    nodes: ctx.nodes,
    score: selected.score,
    elapsedMs: performance.now() - started,
  };
}
